import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OUTLET_COLOR, colorForIndex } from "../../lib/routeColors";
import { apiClient } from "../../lib/apiClient";

// Road-following geometry per saved route (same OSRM endpoint the single-route
// view uses). Keyed on each route's ordered-stop signature so it fetches once
// and never re-fetches on the live position poll (which leaves ordering/coords
// unchanged). Falls back to straight segments when OSRM is unavailable.
function useRoutesRoadLegs(routes) {
  const [legsById, setLegsById] = useState({});
  const sig = routes.map((r) => `${r.id}@${r.geoSig}`).join("|");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        routes.map(async (r) => {
          try {
            const { data } = await apiClient.get(`/routes/${r.id}/geometry`);
            return [r.id, data?.legs || null];
          } catch {
            return [r.id, null];
          }
        })
      );
      if (!cancelled) setLegsById(Object.fromEntries(results));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return legsById;
}

const DELIVERED_COLOR = "#16A34A";
const NOT_DELIVERED_COLOR = "#DC2626";
const PACKING_SKIPPED_COLOR = "#D97706";
const iconCache = new Map();
const CAR_SVG = `<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>`;
const PIN_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="black" stroke="white" stroke-width="1"><path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 6.5 11.5 7.06 12.03a1 1 0 0 0 1.88 0C13.5 21.5 20 15.25 20 10c0-4.42-3.58-8-8-8zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/></svg>`;

function divIcon(color, label, shape = "circle", size = 22, badge = false) {
  const key = `${color}|${label}|${shape}|${size}|${badge}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const radius = shape === "circle" ? "50%" : "4px";
  const badgeHtml = badge
    ? `<div title="Skipped by packing team" style="position:absolute;top:-3px;right:-3px;width:9px;height:9px;border-radius:50%;background:${PACKING_SKIPPED_COLOR};border:1.5px solid white;box-shadow:0 0 0 1px rgba(0,0,0,0.15);"></div>`
    : "";
  const icon = L.divIcon({
    className: "",
    html: `<div style="position:relative;background:${color};width:${size}px;height:${size}px;border-radius:${radius};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.45);display:flex;align-items:center;justify-content:center;color:white;font-size:10px;font-weight:700;font-family:IBM Plex Sans,sans-serif;">${label || ""}${badgeHtml}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
  iconCache.set(key, icon);
  return icon;
}

function carIcon(color) {
  const key = `car|${color}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const icon = L.divIcon({
    className: "",
    html: `<div data-testid="live-map-car-marker" style="position:relative;width:24px;height:24px;">
      <div style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:0.35;" class="animate-ping"></div>
      <div style="position:relative;width:24px;height:24px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 5px rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;">${CAR_SVG}</div>
    </div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  });
  iconCache.set(key, icon);
  return icon;
}

// Searched-address pin (AddressEtaSearch) — solid black, blinks so it stands
// out from every other colored marker on the map.
function searchedIcon() {
  const key = "searched-pin";
  if (iconCache.has(key)) return iconCache.get(key);
  const icon = L.divIcon({
    className: "",
    html: `<div data-testid="live-map-searched-marker" class="animate-pulse" style="width:28px;height:36px;display:flex;align-items:flex-start;justify-content:center;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.6));">${PIN_SVG.replace('width="18" height="18"', 'width="28" height="28"')}</div>`,
    iconSize: [28, 36],
    iconAnchor: [14, 34],
    popupAnchor: [0, -32],
  });
  iconCache.set(key, icon);
  return icon;
}

function timeAgo(epochSecondsStr) {
  const n = Number(epochSecondsStr);
  if (!n) return "never";
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - n);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function toLatLng(lat, lon) {
  const la = parseFloat(lat);
  const lo = parseFloat(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo) || la === 0 || lo === 0) return null;
  return [la, lo];
}

function FitBounds({ points }) {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    const key = points.map((p) => p.join(",")).join("|");
    if (points.length === 0 || lastKey.current === key) return;
    try {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15, animate: false });
      lastKey.current = key;
    } catch {
      /* invalid bounds, ignore */
    }
  }, [points, map]);
  return null;
}

// Live Map redesign (plan.md #4): every route for the selected date at once,
// each in its own driver's persisted color. The portion already delivered is
// always drawn green (reserved exclusively for "completed so far"), and a
// pulsing car icon appears on any route currently being tracked.
export function LiveMapView({ outlet, items, focusedRouteId, searchedPoint }) {
  const outletPt = toLatLng(outlet?.latitude, outlet?.longitude);
  const searchedPt = searchedPoint ? toLatLng(searchedPoint.lat, searchedPoint.lng) : null;

  const routes = items.map((r, idx) => {
    const color = r.driver_color || colorForIndex(idx);
    const validStops = r.stops.map((s) => ({ ...s, pt: toLatLng(s.latitude, s.longitude) })).filter((s) => s.pt);
    const livePt = r.tracking_enabled ? toLatLng(r.live_lat, r.live_lng) : null;
    const pathNodes = [
      ...(outletPt ? [{ pt: outletPt, delivered: true }] : []),
      ...validStops.map((s) => ({ pt: s.pt, delivered: s.delivery_status === 2 })),
    ];
    const segments = [];
    for (let i = 0; i < pathNodes.length - 1; i++) {
      segments.push({ from: pathNodes[i].pt, to: pathNodes[i + 1].pt, green: pathNodes[i + 1].delivered });
    }
    const geoSig = pathNodes.map((n) => n.pt.join(",")).join(";");
    return { ...r, color, validStops, livePt, segments, geoSig };
  });

  const legsById = useRoutesRoadLegs(routes.map((r) => ({ id: r.id, geoSig: r.geoSig })));

  const allPoints = [
    ...(outletPt ? [outletPt] : []),
    ...routes.flatMap((r) => r.validStops.map((s) => s.pt)),
    ...routes.flatMap((r) => (r.livePt ? [r.livePt] : [])),
    ...(searchedPt ? [searchedPt] : []),
  ];

  if (allPoints.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-text-muted" data-testid="live-map-no-data">
        No routes with location data for this date.
      </div>
    );
  }

  return (
    <MapContainer center={allPoints[0]} zoom={12} style={{ height: "100%", width: "100%" }} data-testid="live-map-container">
      <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <FitBounds points={allPoints} />

      {outletPt && (
        <Marker position={outletPt} icon={divIcon(OUTLET_COLOR, "R", "square", 26)}>
          <Popup><p className="text-xs font-semibold">Restaurant — {outlet?.name}</p></Popup>
        </Marker>
      )}

      {searchedPt && (
        <Marker position={searchedPt} icon={searchedIcon()}>
          <Popup><p className="text-xs font-semibold" data-testid="live-map-searched-popup">{searchedPoint?.label || "Searched address"}</p></Popup>
        </Marker>
      )}

      {routes.map((r) => {
        const dimmed = focusedRouteId && focusedRouteId !== r.id;
        const roadLegs = legsById[r.id];
        return (
          <React.Fragment key={r.id}>
            {r.segments.map((seg, i) => {
              // Road-following geometry for this leg when available (legs[i]
              // aligns 1:1 with segment i), else the straight segment.
              const leg = Array.isArray(roadLegs) ? roadLegs[i] : null;
              const positions = leg && leg.length >= 2 ? leg : [seg.from, seg.to];
              return (
                <Polyline
                  key={i} positions={positions}
                  pathOptions={{ color: seg.green ? DELIVERED_COLOR : r.color, weight: dimmed ? 2 : (focusedRouteId === r.id ? 5 : 3), opacity: dimmed ? 0.3 : 0.85 }}
                />
              );
            })}
            {r.validStops.map((s) => {
              const scolor = s.delivery_status === 2 ? DELIVERED_COLOR : s.delivery_status === 3 ? NOT_DELIVERED_COLOR : r.color;
              return (
                <Marker key={s.id} position={s.pt} icon={divIcon(scolor, String(s.delivery_order_id ?? ""), "circle", dimmed ? 11 : (focusedRouteId === r.id ? 20 : 15), s.packing_status === 2)} opacity={dimmed ? 0.4 : 1}>
                  <Popup>
                    <div className="text-xs space-y-0.5" data-testid={`live-map-stop-popup-${s.id}`}>
                      <p className="font-semibold text-text-main">{s.customer_name || `Delivery #${s.id}`}</p>
                      <p className="text-text-muted">{s.address}</p>
                      <p className="text-text-muted">Stop {s.delivery_order_id} · {r.driver_name}</p>
                      {s.packing_status === 2 && (
                        <p className="font-semibold" style={{ color: PACKING_SKIPPED_COLOR }}>Skipped by packing team</p>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {r.livePt && (
              <Marker position={r.livePt} icon={carIcon(r.color)}>
                <Popup>
                  <div className="text-xs" data-testid={`live-map-popup-${r.id}`}>
                    <p className="font-semibold text-text-main">{r.driver_name || `Route #${r.id}`}</p>
                    <p className="text-text-muted">{r.deliveries_done}/{r.deliveries_total} stops done · last ping {timeAgo(r.live_location_at)}</p>
                  </div>
                </Popup>
              </Marker>
            )}
          </React.Fragment>
        );
      })}
    </MapContainer>
  );
}
